import { useCallback, useState } from 'react';
import { useApiServices } from '@shared/lib/use-api-services';
import { isApiError } from '@shared/api/errors/errors';
import type { WalletMonitoringAddress } from '@shared/api/contracts';

export const useAddressAmlActions = () => {
  const services = useApiServices();
  const [pendingIds, setPendingIds] = useState<Set<number>>(new Set());
  const [legacyLoading, setLegacyLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const setAmlMonitoring = async (addressId: number, enabled: boolean) => {
    setPendingIds((prev) => new Set(prev).add(addressId));
    setError(null);

    try {
      const updated = await services.wallets.setWalletAddressAmlMonitoring(addressId, enabled);
      return { success: true as const, data: updated };
    } catch (err: unknown) {
      if (isApiError(err) && err.status === 404) {
        return { success: false as const, notFound: true as const };
      }

      const message = (err as { message?: string })?.message || 'Failed to update AML monitoring';
      setError(message);
      return { success: false as const, error: message };
    } finally {
      setPendingIds((prev) => {
        const next = new Set(prev);
        next.delete(addressId);
        return next;
      });
    }
  };

  /** Legacy modal helpers — not used by wallets monitoring page. */
  const createAml = async (addressId: string | number, isActive = true) => {
    setLegacyLoading(true);
    setError(null);
    try {
      await services.addressAml.createAddressAml({ addressId, isActive });
      return { success: true as const };
    } catch (err: unknown) {
      const message = (err as { message?: string })?.message || 'Failed to add address';
      setError(message);
      return { success: false as const, error: message };
    } finally {
      setLegacyLoading(false);
    }
  };

  const toggleAml = async (id: string | number) => {
    setLegacyLoading(true);
    setError(null);
    try {
      await services.addressAml.toggleAddressAmlActive(id);
      return { success: true as const };
    } catch (err: unknown) {
      const message = (err as { message?: string })?.message || 'Failed to toggle status';
      setError(message);
      return { success: false as const, error: message };
    } finally {
      setLegacyLoading(false);
    }
  };

  const deleteAml = async (id: string | number) => {
    setLegacyLoading(true);
    setError(null);
    try {
      await services.addressAml.deleteAddressAml(id);
      return { success: true as const };
    } catch (err: unknown) {
      const message = (err as { message?: string })?.message || 'Failed to delete';
      setError(message);
      return { success: false as const, error: message };
    } finally {
      setLegacyLoading(false);
    }
  };

  const isPending = (addressId: number) => pendingIds.has(addressId);

  return {
    setAmlMonitoring,
    createAml,
    toggleAml,
    deleteAml,
    isPending,
    isLoading: legacyLoading || pendingIds.size > 0,
    error,
    clearError,
  };
};

export type SetAmlMonitoringResult =
  | { success: true; data: WalletMonitoringAddress }
  | { success: false; notFound: true }
  | { success: false; error: string };
